import { OPS_URLS } from "../apiConfig.js";
import apiClient from "../apiInstance.js";

// ── Helper ────────────────────────────────────────────────────────
// Same shape as pbiservices.js: log the status and body, then rethrow so the
// calling page can show its own error state.
async function get(url, params) {
  try {
    const response = await apiClient.get(url, { params });
    return response.data;
  } catch (err) {
    console.error(`Error fetching ${url}`, err?.response?.status, err?.response?.data || err?.message);
    throw err;
  }
}


// ── Agent Health ──────────────────────────────────────────────────
// No params: this page is always NOW. The tenant zone is resolved server-side
// and echoed back as reportingTimeZone.
export const getOpsLive = () => get(OPS_URLS.OPS_LIVE);


// ── Response Speed ────────────────────────────────────────────────
// Param shape matches the PBI routes minus campaign and channel:
// { startDate, endDate, granularity }. Ops watches one worker's queue, which
// has neither dimension.
export const getResponseSpeedSummary      = (params) => get(OPS_URLS.OPS_RESPONSE_SPEED_SUMMARY,      params);
export const getResponseSpeedTrend        = (params) => get(OPS_URLS.OPS_RESPONSE_SPEED_TREND,        params);
export const getResponseSpeedDistribution = (params) => get(OPS_URLS.OPS_RESPONSE_SPEED_DISTRIBUTION, params);
export const getSlowestResponses          = (params) => get(OPS_URLS.OPS_RESPONSE_SPEED_SLOWEST,      params);


// ── Service Interruptions ─────────────────────────────────────────
export const getInterruptionsSummary = (params) => get(OPS_URLS.OPS_INTERRUPTIONS_SUMMARY, params);
export const getInterruptionsTrend   = (params) => get(OPS_URLS.OPS_INTERRUPTIONS_TREND,   params);
export const getInterruptionsDetail  = (params) => get(OPS_URLS.OPS_INTERRUPTIONS_DETAIL,  params);


// ── Duplicate Watch ───────────────────────────────────────────────
// deliveryAttemptsPerMessage on the trend must read 1.000. Anything higher
// means a customer message reached the agent more than once.
export const getDeliveryTrend         = (params) => get(OPS_URLS.OPS_DELIVERY_TREND,      params);
export const getDeliveryRepeats       = (params) => get(OPS_URLS.OPS_DELIVERY_REPEATS,    params);

// Accounts with more than one CF-AI3-PAY event on the same local day, read
// from customerconvevent rather than matched against the agent's wording.
export const getDuplicatePaymentWatch = (params) => get(OPS_URLS.OPS_PAYMENTS_DUPLICATES, params);

// Transactions whose confirmation number appears nowhere in the conversation.
// The money-moved-silently case — text matching could never see it, because
// there is no message to match.
export const getUnconfirmedPayments   = (params) => get(OPS_URLS.OPS_PAYMENTS_UNCONFIRMED, params);


// ── Payment Activity ──────────────────────────────────────────────
// Counts of confirmation messages, not settlements. Dollar amounts come from
// getDashboardPaymentsSummary in pbiservices, which reads customerconvevent.
export const getPaymentsOpsSummary = (params) => get(OPS_URLS.OPS_PAYMENTS_SUMMARY, params);
export const getPaymentsOpsTrend   = (params) => get(OPS_URLS.OPS_PAYMENTS_TREND,   params);
export const getPaymentsOpsDetail  = (params) => get(OPS_URLS.OPS_PAYMENTS_DETAIL,  params);


// ── Customer Experience ───────────────────────────────────────────
export const getExperienceSummary = (params) => get(OPS_URLS.OPS_EXPERIENCE_SUMMARY, params);
export const getExperienceTrend   = (params) => get(OPS_URLS.OPS_EXPERIENCE_TREND,   params);
export const getExperienceDetail  = (params) => get(OPS_URLS.OPS_EXPERIENCE_DETAIL,  params);


// ── Daily Trend ───────────────────────────────────────────────────
export const getDailyTrend = (params) => get(OPS_URLS.OPS_DAILY_TREND, params);


// ── Drill-down ────────────────────────────────────────────────────
// Full thread plus the payment ledger for one conversation. Per-id path built
// here rather than in apiConfig, matching REMINDER_URLS / REPORT_URLS.
export const getConversationThread = (customerdataid) =>
  get(`${OPS_URLS.OPS_CONVERSATION}/${customerdataid}`);

// ── Spammer ───────────────────────────────────────────────────────
// Grouped by identical message text on one conversation, not by volume.
export const getSpammerSummary = (params) => get(OPS_URLS.OPS_SPAMMERS_SUMMARY, params);
export const getSpammerTrend   = (params) => get(OPS_URLS.OPS_SPAMMERS_TREND,   params);
export const getSpammerDetail  = (params) => get(OPS_URLS.OPS_SPAMMERS_DETAIL,  params);